/**
 * Admin Sidebar
 * Menu lateral do Admin Console
 */
import { useNavigate, useLocation } from 'react-router-dom'
import {
  LayoutDashboard,
  Building2,
  Users,
  CreditCard,
  Receipt,
  GraduationCap,
  BarChart3,
  HeadphonesIcon,
  Settings,
  LogOut,
  Brain,
  Zap,
} from 'lucide-react'
import { useAdminAuth } from '../../hooks/useAdminAuth'

interface MenuItem {
  label: string
  icon: React.ElementType
  path: string
  badge?: string
}

interface MenuSection {
  title: string
  items: MenuItem[]
}

export default function AdminSidebar() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, logout } = useAdminAuth()
  
  const menuSections: MenuSection[] = [
    {
      title: 'Overview',
      items: [
        { label: 'Dashboard', icon: LayoutDashboard, path: '/dashboard' },
        { label: 'Analytics', icon: BarChart3, path: '/analytics' },
      ],
    },
    {
      title: 'Clientes',
      items: [
        { label: 'Empresas', icon: Building2, path: '/companies' },
        { label: 'Usuários', icon: Users, path: '/users' },
        { label: 'Suporte', icon: HeadphonesIcon, path: '/support', badge: '3' },
      ],
    },
    {
      title: 'Financeiro',
      items: [
        { label: 'Planos', icon: CreditCard, path: '/plans' },
        { label: 'Faturamento', icon: Receipt, path: '/billing' },
      ],
    },
    {
      title: 'Plataforma',
      items: [
        { label: 'Treinamentos', icon: GraduationCap, path: '/training' },
        { label: 'Modelos ML', icon: Brain, path: '/ml-models' },
        { label: 'Configurações', icon: Settings, path: '/settings' },
      ],
    },
  ]
  
  const isActive = (path: string) => location.pathname === path
  
  const handleLogout = () => {
    logout()
    navigate('/login')
  }
  
  return (
    <aside className="fixed left-0 top-0 h-screen w-64 bg-ws-bg-secondary border-r border-slate-800 flex flex-col">
      {/* Logo */}
      <div className="h-16 flex items-center px-5 border-b border-slate-800">
        <div className="flex items-center gap-3">
          <div 
            className="w-10 h-10 rounded-xl flex items-center justify-center shadow-lg"
            style={{ background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)' }}
          >
            <Zap className="w-5 h-5 text-white" />
          </div>
          <div className="flex flex-col">
            <span className="text-lg font-bold text-white">
              Work<span className="font-light italic text-violet-400">Stream</span>
            </span>
            <span className="text-[10px] text-violet-400/70 -mt-1 font-mono tracking-wider uppercase">
              Admin Console
            </span>
          </div>
        </div>
      </div>
      
      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto p-3 space-y-6">
        {menuSections.map((section) => (
          <div key={section.title}>
            <h3 className="px-3 mb-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">
              {section.title}
            </h3>
            <ul className="space-y-1">
              {section.items.map((item) => {
                const Icon = item.icon
                const active = isActive(item.path)
                
                return (
                  <li key={item.path}>
                    <button
                      onClick={() => navigate(item.path)}
                      className={`
                        w-full flex items-center gap-3 px-3 py-2.5 rounded-xl
                        transition-all duration-200 group
                        ${active 
                          ? 'bg-violet-500/10 text-violet-400 border border-violet-500/20' 
                          : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'
                        }
                      `}
                    >
                      <Icon className={`w-5 h-5 ${active ? 'text-violet-400' : ''}`} />
                      <span className="text-sm font-medium flex-1 text-left">{item.label}</span>
                      {item.badge && (
                        <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-rose-500/20 text-rose-400">
                          {item.badge}
                        </span>
                      )}
                    </button>
                  </li>
                )
              })}
            </ul>
          </div>
        ))}
      </nav>
      
      {/* User & Logout */}
      <div className="p-3 border-t border-slate-800 space-y-2">
        {/* User Info */}
        <div className="flex items-center gap-3 px-3 py-2">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-white font-bold text-sm">
            {user?.name?.charAt(0) || 'A'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-200 truncate">{user?.name}</p>
            <p className="text-xs text-slate-500 truncate">{user?.role}</p>
          </div>
        </div>
        
        {/* Logout */}
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-slate-400 hover:text-rose-400 hover:bg-rose-500/10 transition-all duration-200"
        >
          <LogOut className="w-5 h-5" />
          <span className="text-sm font-medium">Sair</span>
        </button>
      </div>
    </aside>
  )
}

